/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'
import oPath from 'object-path'

import {REMOVE_SESSION} from './session'
import {computePaginateSortFilterParams, computedCollectionCount} from '@/utils/api'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
import type {JsonApiEntity} from '@/store'

export const REPLACE_NETWORK_PROFILES = 'REPLACE_NETWORK_PROFILES'
export const ADD_NETWORK_PROFILES = 'ADD_NETWORK_PROFILES'
export const MODIFY_NETWORK_PROFILE = 'MODIFY_NETWORK_PROFILE'
export const REMOVE_NETWORK_PROFILE = 'REMOVE_NETWORK_PROFILE'
export const SET_NETWORK_PROFILES_TOTAL = 'SET_NETWORK_PROFILES_TOTAL'

export const FETCH_NETWORK_PROFILES = 'FETCH_NETWORK_PROFILES'
export const FETCH_NETWORK_PROFILE = 'FETCH_NETWORK_PROFILE'
export const CREATE_NETWORK_PROFILE = 'CREATE_NETWORK_PROFILE'
export const UPDATE_NETWORK_PROFILE = 'UPDATE_NETWORK_PROFILE'
export const DELETE_NETWORK_PROFILE = 'DELETE_NETWORK_PROFILE'

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
})

export default {
  state: initialState(),
  getters: {
    networkProfileDefaults: () => ({
      network_profile_id: 'newNetworkProfileID',
    }),
    networkProfiles: ({entities = {}}: Object) =>
      //$FlowFixMe
      Object.keys(entities).map(key => entities[key]),
    // Returns an object with all network-profiles, keyed by network-profile EUI
    networkProfilesKeyedById: (state: Object, getters: Object) => {
      const networkProfiles = {}
      getters.networkProfiles.forEach(dp => {
        networkProfiles[dp.id] = dp
      })
      return networkProfiles
    },
    networkProfilesById: (state: Object) => (id: string) => state.entities[id] || {},
  },
  mutations: {
    RESET_STATE(state: Object) {
      Object.assign(state, initialState())
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [REPLACE_NETWORK_PROFILES](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_NETWORK_PROFILES](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_NETWORK_PROFILE](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_NETWORK_PROFILE](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [SET_NETWORK_PROFILES_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total
    },
  },
  actions: {
    [FETCH_NETWORK_PROFILES]: async function(
      {commit, dispatch}: Object,
      payload: Object
    ) {
      try {
        let pathstring = '/network_profiles'
        if (payload && payload.context && payload.context.orgId) {
          pathstring = '/organizations/' + payload.context.orgId + pathstring
        }

        const {data} = await axios.get(pathstring, {
          params: computePaginateSortFilterParams(payload),
        })

        const {pagination} = data.meta
        commit(SET_NETWORK_PROFILES_TOTAL, {total: pagination.collection_count})
        commit(REPLACE_NETWORK_PROFILES, {
          entities: normalize(data).entities.network_profiles,
        })

        return {
          success: true,
          // Return fetched data as flat array
          items: data.data.map(item => item.attributes),
          collectionCount: computedCollectionCount({
            endRow: payload ? payload.endRow : 0,
            filteredCount: pagination.filtered_count,
            collectionCount: pagination.collection_count,
          }),
        }
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load network profiles ',
          detail: err.message,
          color: 'error',
        })
      }
      return {success: false}
    },
    [FETCH_NETWORK_PROFILE]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {data} = await axios.get(`/network_profiles/${payload.id}`)
        const {entities} = normalize(data)
        commit(ADD_NETWORK_PROFILES, {entities: entities.network_profiles})
        return {success: true}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load network-profile ',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
    [CREATE_NETWORK_PROFILE]: async function(
      {commit, dispatch}: Object,
      payload: JsonApiEntity
    ) {
      try {
        const {data} = await axios.post('/network_profiles', {
          data: {
            type: 'network_profiles',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(ADD_NETWORK_PROFILES, {entities: entities.network_profiles})
        dispatch(NEW_NOTIFICATION, {
          text: 'Saved new network-profile!',
          color: 'success',
        })
        return data.data.attributes.id
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to save network-profile ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [UPDATE_NETWORK_PROFILE]: async function(
      {commit, dispatch}: Object,
      payload: Object
    ) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: 'Saving...',
        color: 'secondary',
        undoable: true,
      })
      try {
console.log(payload.attributes)
        const {data} = await axios.patch(`/network_profiles/${payload.id}`, {
          data: {
            type: 'network_profiles',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_NETWORK_PROFILE, entities.network_profiles[payload.id])
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Saved!',
          color: 'success',
          undoable: true,
        })
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update network-profile ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [DELETE_NETWORK_PROFILE]: async function(
      {commit, dispatch}: Object,
      payload: Object
    ) {
      const notificationId = await dispatch(NEW_NOTIFICATION, {
        text: `Deleting network-profile...`,
        color: 'secondary',
      })

      try {
        await axios.delete(`/network_profiles/${payload.id}`)
        commit(REMOVE_NETWORK_PROFILE, payload)
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Deleted network-profile!`,
          color: 'success',
        })
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: `Failed to delete network-profile `,
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/network_profiles.js